import { ComponentChildren, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { GetConsent } from 'src/ConsentManagement/ConsentStorage';
import { getVendors } from 'src/ConsentManagement/Services/VendorDataService';
import { GetAllOptionIds } from '../../Preferences/PreferenceModel';
import { VendorModel, VendorModels } from '../../Vendors/vendorTypes';

export type Selections = {
  vendorSelections?: VendorModels;
  preferenceSelections?: number[];
};

type UpdateSelections = {
  setVendorSelections: (vendorSelections: VendorModel[]) => void;
  setVendor: (vendor: VendorModel) => void;
  setPreferenceSelections: (preferenceSelections: number[]) => void;
};

export const SelectionContext = createContext<Selections>(null);

export const UpdateSelectionContext = createContext<UpdateSelections>(null);

const areAllPreferencesOn = (input: number[]) => {
  const allOptions = GetAllOptionIds();

  return allOptions.reduce((result, item) => {
    const isSelected = (input ?? []).indexOf(item) >= 0;
    return isSelected ? result : false;
  }, true);
};

const getInitialState = async () => {
  const vendorSelections = await getVendors();
  return { vendorSelections };
};

export const SelectionContextProvider = ({ children }: { children?: ComponentChildren }) => {
  const [selections, setSelections] = useState<Selections>({
    vendorSelections: new VendorModels(),
    preferenceSelections: [],
  });

  const setVendorSelections = (vendorSelections: VendorModel[]) => {
    setSelections((prevState) => {
      const newSelections = { ...prevState };
      newSelections.vendorSelections = new VendorModels(vendorSelections);
      switch (newSelections.vendorSelections.GetOptInStatus()) {
        case 'all':
          newSelections.preferenceSelections = GetAllOptionIds();
          break;
        case 'none':
          newSelections.preferenceSelections = [];
          break;
        case 'some':
          const isPreferenceStack1On = newSelections.preferenceSelections.indexOf(1) >= 0;
          if (isPreferenceStack1On) break;
          newSelections.preferenceSelections.push(1);
          break;
      }

      return newSelections;
    });
  };

  const setVendor = (vendor: VendorModel) => {
    const vendors = [...(selections.vendorSelections?.models ?? [])];
    const existinVendor = vendors.filter((v) => v.vendorId === vendor.vendorId)[0];
    const index = vendors.indexOf(existinVendor);

    vendors[index] = vendor;
    setVendorSelections(vendors);
  };

  const setPreferenceSelections = (input: number[]) => {
    setSelections((prevState) => {
      const newSelections = { ...prevState };
      newSelections.preferenceSelections = input;
      if (areAllPreferencesOn(newSelections.preferenceSelections)) {
        newSelections.vendorSelections = prevState.vendorSelections.SetAllVendorsOn();
      }
      return newSelections;
    });
  };

  useEffect(() => {
    const setInitialState = async () => {
      try {
        const { vendorSelections } = await getInitialState();
        const newSelections: Selections = {
          vendorSelections,
          preferenceSelections: GetConsent(),
        };
        setSelections(newSelections);
      } catch (error) {
        console.error('Could not set vendors', error);
      }
    };

    setInitialState();
  }, []);

  const updaters = { setVendorSelections, setVendor, setPreferenceSelections };

  return (
    <SelectionContext.Provider value={selections}>
      <UpdateSelectionContext.Provider value={updaters}>{children ?? null}</UpdateSelectionContext.Provider>
    </SelectionContext.Provider>
  );
};
